import { Clock, MessageSquare } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { Bloco } from '@/types/bloco'
import { BlocoLabelChip } from './BlocoLabelChip'
import { hexToRgba, normalizeLabelColor } from './blocoLabelUtils'
import { formatBlocoDueLabel } from './blocosUtils'

interface TarefasKanbanCardProps {
  bloco: Bloco
  currentUserId?: string
  isDragging?: boolean
  onOpen: (id: string) => void
  onDragStart: (e: React.DragEvent, blocoId: string) => void
  onDragEnd: () => void
}

export function TarefasKanbanCard({
  bloco,
  currentUserId,
  isDragging,
  onOpen,
  onDragStart,
  onDragEnd,
}: TarefasKanbanCardProps) {
  const dueLabel = formatBlocoDueLabel(bloco)
  const awaitingYou = currentUserId && bloco.pendingUserId === currentUserId
  const messageCount = bloco.messageCount ?? 0
  const labels = bloco.labels ?? []
  const primaryColor = labels[0] ? normalizeLabelColor(labels[0].color) : null

  return (
    <article
      draggable
      onDragStart={(e) => onDragStart(e, bloco.id)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(bloco.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(bloco.id)
        }
      }}
      role='button'
      tabIndex={0}
      className={cn(
        'cursor-grab overflow-hidden rounded-lg border bg-bg-card shadow-sm transition-all active:cursor-grabbing',
        'hover:shadow-md',
        isDragging && 'opacity-50',
        primaryColor ? 'border-transparent' : 'border-[var(--glass-border)] hover:border-[var(--brand-primary)]/30',
      )}
      style={
        primaryColor
          ? {
              borderLeft: `4px solid ${primaryColor}`,
              backgroundColor: hexToRgba(primaryColor, 0.1),
              boxShadow: `inset 0 1px 0 ${hexToRgba(primaryColor, 0.25)}`,
            }
          : undefined
      }
    >
      {labels.length > 0 && (
        <div className='flex gap-1 px-2 pt-2'>
          {labels.map((label) => (
            <span
              key={label.id}
              className='h-1.5 min-w-[28px] flex-1 rounded-full'
              style={{ backgroundColor: normalizeLabelColor(label.color) }}
              title={label.name}
            />
          ))}
        </div>
      )}

      <div className={cn('p-3', labels.length > 0 && 'pt-2')}>
        {labels.length > 0 && (
          <div className='mb-2 flex flex-wrap gap-1'>
            {labels.map((label) => (
              <BlocoLabelChip key={label.id} label={label} className='text-[10px]' />
            ))}
          </div>
        )}

        <p className='text-sm font-semibold leading-snug text-text-primary'>
          {bloco.title || bloco.subject}
        </p>
        {bloco.subject && bloco.title !== bloco.subject && (
          <p className='mt-1 text-xs text-text-muted'>{bloco.subject}</p>
        )}

        <div className='mt-2 flex flex-wrap items-center gap-2 text-[10px] text-text-muted'>
          {dueLabel && (
            <span className='inline-flex items-center gap-1'>
              <Clock size={11} />
              {dueLabel}
            </span>
          )}
          {messageCount > 0 && (
            <span className='inline-flex items-center gap-1'>
              <MessageSquare size={11} />
              {messageCount}
            </span>
          )}
          {awaitingYou && (
            <span className='rounded bg-amber-500/15 px-1.5 py-0.5 font-medium text-amber-600'>
              Sua vez
            </span>
          )}
          {bloco.pendingUser && !awaitingYou && bloco.status === 'open' && (
            <span className='rounded bg-[var(--glass-bg)] px-1.5 py-0.5'>
              Aguardando {bloco.pendingUser.name.split(' ')[0]}
            </span>
          )}
        </div>
      </div>
    </article>
  )
}
